import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Divider,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Avatar,
  Grid,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { ExpandLess, ExpandMore, Delete, GroupAdd, Edit, PersonAdd, Refresh, Search } from '@mui/icons-material';
import axios from 'axios';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import isEqual from 'lodash/isEqual';

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pendingTeams, setPendingTeams] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [maids, setMaids] = useState([]);
  const [maidsLoading, setMaidsLoading] = useState(false);
  const [maidSearchTerm, setMaidSearchTerm] = useState('');
  const [selectedMaids, setSelectedMaids] = useState([]);
  const [addMemberTeamId, setAddMemberTeamId] = useState(null);

  // Fetch teams from backend
  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const response = await axios.get(`${apiBase}/api/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTeams(response.data.data);
        setPendingTeams(response.data.data.map(team => ({ ...team })));
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Fetch maids for add member dialog
  const fetchMaids = async (searchTerm = '') => {
    setMaidsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const params = new URLSearchParams({
        limit: 100, // Get more maids for selection
        status: 'active', // Only active maids
        sortBy: 'firstName',
        sortOrder: 'ASC'
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(`${apiBase}/api/maids?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle new paginated response format
      const maidsList = response.data?.data || response.data || [];
      setMaids(maidsList);
    } catch (err) {
      console.error('Error fetching maids:', err);
      setMaids([]);
    } finally {
      setMaidsLoading(false);
    }
  };

  // Toggle expand/collapse for team members
  const handleToggle = (teamId) => {
    setExpanded((prev) => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  // Remove a team
  const handleRemoveTeam = async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      await axios.delete(`${apiBase}/api/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTeams((prev) => prev.filter((team) => team.id !== teamId));
    } catch (err) {
      console.error('Error deleting team:', err);
      setError('Failed to delete team');
    }
  };

  // Open create team dialog
  const handleAddTeam = () => {
    setFormData({ name: '', description: '' });
    setCreateDialogOpen(true);
    setError('');
  };

  // Open edit team dialog
  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setFormData({ name: team.name, description: team.description || '' });
    setEditDialogOpen(true);
    setError('');
  };

  // Create new team
  const handleCreateTeam = async () => {
    try {
      setSubmitLoading(true);
      setError('');

      if (!formData.name.trim()) {
        setError('Team name is required');
        return;
      }

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const response = await axios.post(`${apiBase}/api/teams`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTeams((prev) => [...prev, response.data.data]);
        setCreateDialogOpen(false);
        setFormData({ name: '', description: '' });
      }
    } catch (err) {
      console.error('Error creating team:', err);
      setError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Update existing team
  const handleUpdateTeam = async () => {
    try {
      setSubmitLoading(true);
      setError('');

      if (!formData.name.trim()) {
        setError('Team name is required');
        return;
      }

      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
      
      const response = await axios.put(`${apiBase}/api/teams/${editingTeam.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTeams((prev) => prev.map(team => 
          team.id === editingTeam.id 
            ? { ...team, ...response.data.data }
            : team
        ));
        setEditDialogOpen(false);
        setEditingTeam(null);
        setFormData({ name: '', description: '' });
      }
    } catch (err) {
      console.error('Error updating team:', err);
      setError(err.response?.data?.error || 'Failed to update team');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchTeams();
  };

  // When adding/removing members, update pendingTeams
  const handleAddMemberToTeam = (teamId, maid) => {
    setPendingTeams(prev => prev.map(team =>
      team.id === teamId && !team.members.some(m => m.id === maid.id)
        ? { ...team, members: [...team.members, maid] }
        : team
    ));
  };
  const handleRemoveMemberFromTeam = (teamId, maidId) => {
    setPendingTeams(prev => prev.map(team =>
      team.id === teamId
        ? { ...team, members: team.members.filter(m => m.id !== maidId) }
        : team
    ));
  };

  // Open add member dialog
  const handleOpenAddMember = (teamId) => {
    setAddMemberTeamId(teamId);
    setSelectedMaids([]);
    setMaids([]);
    setMaidSearchTerm('');
    setAddMemberDialogOpen(true);
  };

  // Handle maid search in dialog
  const handleMaidSearch = async () => {
    if (!maidSearchTerm.trim()) {
      alert('Please enter a search term');
      return;
    }
    await fetchMaids(maidSearchTerm.trim());
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setMaidSearchTerm(event.target.value);
  };

  // Handle Enter key press in search
  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleMaidSearch();
    }
  };

  // Add selected maids to team in pendingTeams
  const handleAddMembersToPendingTeam = () => {
    if (!addMemberTeamId || selectedMaids.length === 0) return;
    setPendingTeams(prev => prev.map(team =>
      team.id === addMemberTeamId
        ? { ...team, members: [...team.members, ...selectedMaids.filter(maid => !team.members.some(m => m.id === maid.id))] }
        : team
    ));
    setAddMemberDialogOpen(false);
    setAddMemberTeamId(null);
    setSelectedMaids([]);
  };

  // Utility to check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (teams.length !== pendingTeams.length) return true;
    for (let i = 0; i < teams.length; i++) {
      const t = teams[i];
      const p = pendingTeams.find(pt => pt.id === t.id);
      if (!p || !isEqual({ ...t, members: t.members?.map(m => m.id).sort() }, { ...p, members: p.members?.map(m => m.id).sort() })) {
        return true;
      }
    }
    return false;
  };

  // Save all pending team changes using batch endpoint
  const handleSaveTeams = async () => {
    setSaveLoading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
    try {
      const updatedTeams = [];
      for (const team of pendingTeams) {
        const response = await axios.put(`${apiBase}/api/teams/${team.id}`,
          {
            name: team.name,
            description: team.description,
            members: team.members.map(m => m.userId)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.data) {
          updatedTeams.push(response.data.data);
        }
      }
      setTeams(updatedTeams);
      setPendingTeams(updatedTeams.map(team => ({ ...team })));
    } catch (err) {
      setError('Failed to save teams');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <AdminPageLayout
      title="Team Management"
      subtitle="Organize your service providers into teams for better coordination and service delivery."
      maxWidth="lg"
    >
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GroupAdd />}
            onClick={handleAddTeam}
          >
            Add Team
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSaveTeams}
            disabled={saveLoading || !hasUnsavedChanges()}
          >
            {saveLoading ? <CircularProgress size={20} /> : 'Commit Changes'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : teams.length === 0 ? (
        <Alert severity="info">
          No teams found. Create your first team to get started!
        </Alert>
      ) : (
        <List>
              {pendingTeams.map((team) => (
                <React.Fragment key={team.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="h6">{team.name}</Typography>
                          <Chip 
                            label={`${team.members?.length || 0} member${(team.members?.length || 0) !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      }
                      secondary={team.description}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleEditTeam(team)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleToggle(team.id)}>
                        {expanded[team.id] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <IconButton edge="end" color="error" onClick={() => handleRemoveTeam(team.id)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Collapse in={expanded[team.id]} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4, py: 2, bgcolor: 'grey.50' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Team Members
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<PersonAdd />}
                          onClick={() => handleOpenAddMember(team.id)}
                        >
                          Add Member
                        </Button>
                      </Stack>
                      {(!team.members || team.members.length === 0) ? (
                        <Typography variant="body2" color="text.secondary">
                          No members in this team yet. Click "Add Member" to assign technicians to this team.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {team.members.map((member) => (
                            <Grid item xs={12} sm={6} key={member.id}>
                              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ width: 40, height: 40 }}>
                                  {member.firstName?.[0] || member.name?.[0] || 'M'}
                                </Avatar>
                                <Box flex={1}>
                                  <Typography variant="body1" fontWeight={500}>
                                    {member.firstName ? `${member.firstName} ${member.lastName}` : member.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {member.email}
                                  </Typography>
                                </Box>
                                <IconButton size="small" color="error" onClick={() => handleRemoveMemberFromTeam(team.id, member.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  </Collapse>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Create Team Dialog */}
          <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Team Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateTeam}
                variant="contained"
                disabled={submitLoading}
              >
                {submitLoading ? <CircularProgress size={20} /> : 'Create Team'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Team Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Team Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                margin="normal"
                multiline
                rows={3}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateTeam}
                variant="contained"
                disabled={submitLoading}
              >
                {submitLoading ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Member Dialog */}
          <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Add Members to Team</DialogTitle>
            <DialogContent>
              {/* Search Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Search for technicians by name or email to add them to the team.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Search technicians by name or email"
                    value={maidSearchTerm}
                    onChange={handleSearchChange}
                    onKeyPress={handleSearchKeyPress}
                    placeholder="e.g., Maria Garcia or maria@example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleMaidSearch}
                    disabled={maidsLoading || !maidSearchTerm.trim()}
                    startIcon={maidsLoading ? <CircularProgress size={20} /> : <Search />}
                  >
                    {maidsLoading ? 'Searching...' : 'Search'}
                  </Button>
                </Stack>
              </Box>

              {/* Results Section */}
              {maidsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Searching for technicians...</Typography>
                </Box>
              ) : maids.length === 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center" minHeight={120} justifyContent="center">
                  <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography color="textSecondary" align="center">
                    {maidSearchTerm ? `No technicians found matching "${maidSearchTerm}"` : 'Search for technicians to add to the team'}
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={maids}
                  getOptionLabel={(option) => `${option.user?.firstName} ${option.user?.lastName} (${option.user?.email})`}
                  value={selectedMaids}
                  onChange={(_, value) => setSelectedMaids(value)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={`Select from ${maids.length} found technicians`}
                      placeholder="Choose technicians to add..." 
                      margin="normal" 
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {option.user?.firstName?.[0]}{option.user?.lastName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {option.user?.firstName} {option.user?.lastName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {option.user?.email} • {option.onboardingStatus}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  disableCloseOnSelect
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleAddMembersToPendingTeam}
                variant="contained"
                disabled={maidsLoading || selectedMaids.length === 0}
              >
                Add {selectedMaids.length > 0 ? `${selectedMaids.length} ` : ''}Member{selectedMaids.length !== 1 ? 's' : ''}
              </Button>
            </DialogActions>
          </Dialog>
    </AdminPageLayout>
  );
};

export default TeamManagement; 